import { z } from "zod";
import { organizationIntegrationAccountEntitySchema } from "./organization-integration.schema";

/**
 * Provider-agnostic connection source. `plaid` covers `plaid_connection`; the
 * rest map from the native `ConnectionProvider` enum (`integration_connection`).
 */
export const unifiedConnectionProviderSchema = z.enum([
    "plaid",
    "paypal",
    "mercury",
    "quickbooks",
    "billcom",
    "gusto",
    "stripe",
    "ramp",
    "adp_run",
]);

export type UnifiedConnectionProvider = z.infer<
    typeof unifiedConnectionProviderSchema
>;

/**
 * Providers that can appear as a filterable connection in the org-integrations
 * list: the transaction-feed providers only. QuickBooks (accounting only) never
 * produces assignable accounts and is gated out of the connection list on the
 * backend (`TRANSACTION_FEED_PROVIDERS` in
 * `@common/helpers/integrationConnection`), so offering it as a filter would
 * only ever match nothing. Keep this in sync with that gate when adding a
 * provider.
 */
export const filterableConnectionProviderSchema = z.enum([
    "plaid",
    "paypal",
    "mercury",
    "billcom",
    "gusto",
    "stripe",
    "ramp",
    "adp_run",
]);

export type FilterableConnectionProvider = z.infer<
    typeof filterableConnectionProviderSchema
>;

/**
 * Derived, not stored: `disconnected` when the connection has an error,
 * `unassigned` when it is healthy but no visible account is assigned to an
 * entity, otherwise `connected`.
 */
export const unifiedConnectionStatusSchema = z.enum([
    "connected",
    "unassigned",
    "disconnected",
]);

export type UnifiedConnectionStatus = z.infer<
    typeof unifiedConnectionStatusSchema
>;

/**
 * An account under a unified connection — the lean shape the redesigned table
 * needs, backing every account-level column. Kept lean so the response can be
 * validated at runtime (`validateResponses: true`).
 */
export const organizationUnifiedConnectionAccountSchema = z.object({
    id: z.number(),
    entityId: z.number().nullable(),
    name: z.string().nullable(),
    accountNumberMask: z.string().nullable(),
    isBusiness: z.boolean().nullable(),
    type: z.string().nullable(),
    lastSyncedAt: z.string().datetime({ offset: true }).nullable(),
    hasStatements: z.boolean(),
    entity: organizationIntegrationAccountEntitySchema.nullable(),
});

export type OrganizationUnifiedConnectionAccount = z.infer<
    typeof organizationUnifiedConnectionAccountSchema
>;

/**
 * One connection (a single institution login) unioned across Plaid and native
 * providers, with its accounts. `id` is globally unique across providers
 * (`plaid:<int>` for Plaid, the native uuid otherwise) so rows can be keyed
 * without knowing the source; `providerConnectionId` carries the raw id for
 * provider-specific follow-up actions.
 */
export const organizationUnifiedConnectionSchema = z.object({
    id: z.string(),
    provider: unifiedConnectionProviderSchema,
    providerConnectionId: z.string(),
    login: z.object({
        institutionName: z.string().nullable(),
        institutionLogoUrl: z.string().nullable(),
        email: z.string().nullable(),
    }),
    /**
     * `userId` is the only reliable way to answer "did I connect this?" — both
     * `email` here and the caller's own email are nullable, so they cannot carry
     * that decision. It also lets the server authorize connection-scoped
     * actions instead of trusting the client's judgement.
     */
    connectedBy: z
        .object({
            userId: z.number().nullable(),
            name: z.string().nullable(),
            email: z.string().nullable(),
            avatar: z.string().nullable(),
            iconColor: z.string(),
        })
        .nullable(),
    status: unifiedConnectionStatusSchema,
    error: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    lastSyncedAt: z.string().datetime({ offset: true }).nullable(),
    hasHealthySibling: z.boolean(),
    hasStatements: z.boolean(),
    accounts: z.array(organizationUnifiedConnectionAccountSchema),
});

export type OrganizationUnifiedConnection = z.infer<
    typeof organizationUnifiedConnectionSchema
>;

export const organizationUnifiedConnectionsResponseSchema = z.object({
    connections: z.array(organizationUnifiedConnectionSchema),
    total: z.number(),
    page: z.number(),
    pageCount: z.number(),
});

export type OrganizationUnifiedConnectionsResponse = z.infer<
    typeof organizationUnifiedConnectionsResponseSchema
>;
